import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';

export function PageHeader({ title, subtitle, action, back }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; back?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3.5 min-w-0">
        {back && (
          <button
            type="button"
            onClick={back}
            aria-label={t('common.a11y.back')}
            className="w-[38px] h-[38px] rounded-[11px] bg-surface border border-line flex items-center justify-center text-ink-2 hover:bg-app shrink-0"
          >
            <Icon name="arrowLeft" size={18} />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-[25px] font-bold tracking-[-0.5px] leading-tight truncate">{title}</h1>
          {subtitle && <div className="text-sm text-muted-2 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2 bg-surface border border-line rounded-btn px-3.5 py-2.5 text-muted-3 focus-within:border-primary', className)}>
      <Icon name="search" size={17} />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-muted-3 min-w-0"
      />
    </div>
  );
}

export function EmptyState({ icon = 'file', title, body, action }: { icon?: IconName; title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-14 h-14 rounded-2xl bg-primary-soft text-primary flex items-center justify-center mb-4">
        <Icon name={icon} size={26} />
      </div>
      <div className="font-display font-bold text-base">{title}</div>
      {body && <div className="text-sm text-muted-2 mt-1 max-w-sm">{body}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-muted-2 text-sm">
      <Spinner className="text-primary" /> {label}
    </div>
  );
}

export function ErrorBlock({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center px-6">
      <Icon name="alert" className="text-status-error" size={26} />
      <div className="text-sm text-muted max-w-md">{message}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="text-primary font-bold text-sm">
          {retryLabel}
        </button>
      )}
    </div>
  );
}

export function KV({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('kv', className)}>
      <span className="shrink-0">{label}</span>
      <b className="min-w-0 [overflow-wrap:anywhere]">{children}</b>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-line-soft rounded-md', className)} />;
}
